import { AxiosError } from 'axios';
import { useCallback } from 'react';

import { parseObjectiveDraft } from '../objectiveDraft';

import type { AiResultNotification } from '@/api/ai-gateway/aiHub';
import type {
    ObjectiveDraft,
    ObjectiveDraftLanguage,
    ObjectiveDraftLimitResponse,
} from '@/types/objectives';

import objectiveService from '@/api/objectives/objectiveService';
import { useAiResultContext } from '@/hooks/ai/AiResultProvider';

/**
 * Give up waiting for the SignalR result after this long, so the modal never hangs on a lost or
 * failed job. Matches the backend's own 90-second in-flight window.
 */
export const DRAFT_RESULT_TIMEOUT_MS = 90_000;

interface GenerateObjectiveDraftInput {
    userIntent: string;
    /** The output language the manager chose in the modal. */
    language: ObjectiveDraftLanguage;
    pillarIds: string[];
    employeeId?: string;
}

/**
 * What a generation ends in: a usable draft; a plain failure (rejection, lost job, timeout,
 * unusable payload), or the day's allowance being spent, which the modal words differently.
 */
export type GenerateObjectiveDraftOutcome =
    | { kind: 'draft'; draft: ObjectiveDraft }
    | { kind: 'failed' }
    | { kind: 'limitReached'; limit: number };

const FAILED: GenerateObjectiveDraftOutcome = { kind: 'failed' };

const limitReachedFrom = (error: unknown): GenerateObjectiveDraftOutcome | null => {
    if (!(error instanceof AxiosError) || error.response?.status !== 429) {
        return null;
    }
    const body = error.response.data as Partial<ObjectiveDraftLimitResponse> | undefined;

    return { kind: 'limitReached', limit: body?.limit ?? 0 };
};

/**
 * Generates an objective draft from the manager's intent: posts the request, waits for the AI
 * result over SignalR (matched by correlationId) and returns it parsed. Never throws: every way
 * of not getting a draft is an outcome the modal can word, instead of hanging. Same shape as
 * `useRecommendationsGeneration`, the module's precedent for async AI features.
 */
export function useGenerateObjectiveDraft() {
    const { subscribe } = useAiResultContext();

    const generate = useCallback(
        async (input: GenerateObjectiveDraftInput): Promise<GenerateObjectiveDraftOutcome> => {
            const correlationId = crypto.randomUUID();

            // Arm the subscription before posting, so a fast result can't slip through before we
            // listen. `cancel` releases both the listener and the timer when nothing will arrive.
            let cancel = () => {};
            const resultPromise = new Promise<AiResultNotification | null>((resolve) => {
                let unsubscribe = () => {};
                const timeout = window.setTimeout(() => {
                    unsubscribe();
                    resolve(null);
                }, DRAFT_RESULT_TIMEOUT_MS);
                unsubscribe = subscribe((notification) => {
                    if (notification.correlationId !== correlationId) return;
                    window.clearTimeout(timeout);
                    unsubscribe();
                    resolve(notification);
                });
                cancel = () => {
                    window.clearTimeout(timeout);
                    unsubscribe();
                    resolve(null);
                };
            });

            try {
                const acknowledgement = await objectiveService.generateObjectiveDraft({
                    correlationId,
                    userIntent: input.userIntent,
                    pillarIds: input.pillarIds,
                    employeeId: input.employeeId,
                    language: input.language,
                });
                if (acknowledgement.status !== 'accepted') {
                    cancel();
                    return FAILED;
                }
            } catch (error) {
                cancel();
                return limitReachedFrom(error) ?? FAILED;
            }

            const notification = await resultPromise;
            if (!notification?.success || !notification.content) return FAILED;

            const draft = parseObjectiveDraft(notification.content);

            return draft ? { kind: 'draft', draft } : FAILED;
        },
        [subscribe],
    );

    return { generate };
}
